import ButtonMenuItem from "@components/buttonMenu/ButtonMenuItem";
import MySwitchField from "@components/form/switch";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  Menu,
  Typography,
} from "@mui/material";
import { ChevronDown, Save, Send } from "lucide-react";
import { useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router";
import { useCommonStyles } from "../hooks/useCommonStyles";
import { PreviewPdfDialog } from "./PreviewPdfDialog";

interface IManualDocumentActionsProps {
  translate: (text: any, options?: any) => string;
  isSubmitting: boolean;
  isSubmittingNew: boolean;
  isSubmittingDraft: boolean;
  isLoading: boolean;
  isLegalRepValid: boolean;
  isRecurring: boolean;
  onRecurringChange: (value: boolean) => void;
  showRecurringToggle?: boolean;
  onSubmit: () => void;
  onSubmitAndCreateNew: () => void;
  onSaveAsDraft: (values: any) => Promise<void>;
  showSubmitAndCreateNew?: boolean;
  methods: UseFormReturn<any>;
}

export const ManualDocumentActions = (props: IManualDocumentActionsProps) => {
  const {
    translate,
    isSubmitting,
    isSubmittingNew,
    isSubmittingDraft,
    isLoading,
    isLegalRepValid,
    isRecurring,
    onRecurringChange,
    showRecurringToggle = true,
    showSubmitAndCreateNew = true,
    onSubmit,
    onSubmitAndCreateNew,
    methods,
    onSaveAsDraft,
  } = props;
  const [openSavePopper, setOpenSavePopper] = useState(false);
  const [saveAnchor, setSaveAnchor] = useState<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const { getValues } = methods;

  const navigate = useNavigate();
  const { inputContainer } = useCommonStyles();

  const submitDisabled = isSubmittingNew || isLoading || !isLegalRepValid;
  const createNewDisabled = isSubmitting || isLoading || !isLegalRepValid;
  const submitDraftDisabled =
    isSubmittingDraft || isLoading || !isLegalRepValid;

  return (
    <Grid container spacing={3} sx={{ pb: "24px" }}>
      <Grid item xs={12}>
        <Divider />
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {showRecurringToggle ? (
            <Box sx={{ ...inputContainer, p: 0 }}>
              <MySwitchField
                checked={isRecurring}
                onChange={(event) => onRecurringChange(event.target.checked)}
                label={
                  <Typography variant="body2">
                    {translate("make_recurring_invoice")}
                  </Typography>
                }
                labelPlacement="end"
                sx={{ alignItems: "center" }}
              />
            </Box>
          ) : (
            <Box />
          )}

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(-1)}
            >
              {translate("cancel")}
            </Button>
            <PreviewPdfDialog
              {...{ translate, onSubmit, isSubmitting, submitDisabled }}
            />
            <LoadingButton
              variant="outlined"
              size="small"
              loading={isSubmittingDraft}
              disabled={submitDisabled || createNewDisabled}
              onClick={() => onSaveAsDraft(getValues())}
              endIcon={<Save strokeWidth="1.5px" size="18px" />}
            >
              {translate("save_as_draft")}
            </LoadingButton>
            <ButtonGroup
              variant="contained"
              ref={anchorRef}
              aria-label="Button group with a nested menu"
            >
              <LoadingButton
                variant="contained"
                size="small"
                loading={isSubmitting || isSubmittingNew}
                disabled={
                  submitDisabled || createNewDisabled || submitDraftDisabled
                }
                onClick={onSubmit}
                endIcon={<Send strokeWidth="1.5px" size="18px" />}
              >
                {translate("submit")}
              </LoadingButton>
              {showSubmitAndCreateNew && (
                <Button
                  size="small"
                  sx={{
                    padding: "0px 0px 0px 0px",
                  }}
                  disabled={
                    submitDisabled || createNewDisabled || submitDraftDisabled
                  }
                  onClick={() => {
                    setOpenSavePopper((prev) => !prev);
                    if (anchorRef.current) {
                      setSaveAnchor(anchorRef.current);
                    }
                  }}
                >
                  <ChevronDown strokeWidth="1.5px" size="18px" />
                </Button>
              )}
            </ButtonGroup>
            <Menu
              id="basic-menu"
              anchorEl={saveAnchor}
              open={openSavePopper}
              onClose={() => {
                setSaveAnchor(null);
                setOpenSavePopper(false);
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <ButtonMenuItem
                disabled={
                  submitDisabled || createNewDisabled || submitDraftDisabled
                }
                onClick={() => {
                  setOpenSavePopper((prev) => !prev);

                  onSubmit();
                }}
                title={translate("submit")}
              />
              <ButtonMenuItem
                disabled={
                  submitDisabled || createNewDisabled || submitDraftDisabled
                }
                onClick={() => {
                  setOpenSavePopper((prev) => !prev);
                  onSubmitAndCreateNew();
                }}
                title={translate("submit_and_create_new_document")}
              />
            </Menu>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};
